import axios, { CancelToken as AxiosCancelToken, AxiosError, AxiosRequestConfig } from 'axios';
import { AppConfig } from 'config';
import { actions, store } from 'store';

export type CancelToken = AxiosCancelToken;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RequestError<T = { message: string; statusCode: number; error: string }> = AxiosError<T>;
export type RequestConfig = AxiosRequestConfig & { useToken?: boolean };

const httpClient = axios.create({
  baseURL: AppConfig.BASE_API_URL,
  responseType: 'json',
  validateStatus: (status) => status <= 500,
  timeout: 20000,
});

httpClient.interceptors.request.use(async (config: RequestConfig) => {
  const accessToken = await store.getState().auth.accessToken;

  // Payment API request debugging
  if (config.url?.includes('payments')) {
    console.log('[API Interceptor] Request to payment endpoint:', config.url);
    console.log('[API Interceptor] config.useToken:', config.useToken);
    console.log('[API Interceptor] accessToken available:', !!accessToken);

    // Log request data for debugging
    if (config.data) {
      console.log('[API Interceptor] Payment request data:', JSON.stringify(config.data, null, 2));

      // Specifically check for click IDs
      const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
      console.log('[API Interceptor] Click IDs in request:', {
        impactClickId: data.impactClickId || 'missing',
        googleClickId: data.googleClickId || 'missing',
      });
    } else {
      console.log('[API Interceptor] No request data found in payment request');
    }
  }

  if (config.useToken === false || !accessToken) {
    // --- BEGIN ADDED LOGGING ---
    if (config.url?.includes('payments')) {
      console.log('[API Interceptor] Not attaching token for payment endpoint.');
    }
    // --- END ADDED LOGGING ---
    return config;
  } else {
    // --- BEGIN ADDED LOGGING ---
    if (config.url?.includes('payments')) {
      console.log('[API Interceptor] Attaching token for payment endpoint.');
    }
    // --- END ADDED LOGGING ---
    const headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };
    return { ...config, headers };
  }
});

httpClient.interceptors.response.use(
  (response) => {
    if (response.status === 401) {
      store.dispatch(actions.auth.signOut());
    }

    return response;
  },
  (error) => {
    type AxiosErrorWithData = AxiosError & { data?: unknown };
    type WrappedRequestError = Error & { data?: unknown; status?: number; url?: string };

    const asString = (value: unknown): string => {
      if (typeof value === 'string') return value;
      if (value instanceof Error) return value.message;
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };

    const extractMessage = (data: unknown, fallback: string): string => {
      if (!data || typeof data !== 'object') return fallback;

      const maybeMessage = (data as { message?: unknown }).message;
      if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;
      if (Array.isArray(maybeMessage) && typeof maybeMessage[0] === 'string' && maybeMessage[0].trim()) {
        return maybeMessage[0];
      }

      return fallback;
    };

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data ?? (error as AxiosErrorWithData).data;
      const fallbackMessage = error.message || 'Request failed';
      const message = extractMessage(data, fallbackMessage);

      if (status === 401) {
        store.dispatch(actions.auth.signOut());
      }

      const wrapped = new Error(message) as WrappedRequestError;
      wrapped.data = data;
      wrapped.status = status;
      wrapped.url = error.config?.url;

      throw wrapped;
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(asString(error));
  },
);

export const createSourceCancelToken = () => {
  return axios.CancelToken.source();
};

export default httpClient;
